import { makeAutoObservable, runInAction } from "mobx";
import { apiService } from "../services";
import type { ApiResponse } from "../services";
import { networkEventManager } from "../services/networkEventManager";
import type {
  ITicket,
  ITicketDetail,
  TicketChangedFrame,
  TicketColumnFilters,
  TicketsTableColumnId,
  TicketsTableColumnSetting,
} from "../types/tickets";
import { TICKETS_TABLE_COLUMN_OPTIONS } from "../types/tickets";
import type { FilterOperator } from "../types/table";
import type MainStore from "./MainStore";
import { WS_EVENTS } from "../types/wsEvents";

const COLUMN_VISIBILITY_STORAGE_KEY = "ticketsTableColumnVisibility";

const COLUMN_FILTER_TYPES: Record<string, "dateRange" | "priceRange"> = {
  created_at: "dateRange",
  updated_at: "dateRange",
  replies: "priceRange",
};

const PER_PAGE = 25;
const SEED_TTL_MS = 5 * 60 * 1000;

export interface TicketOrderBy {
  field: string;
  direction: "asc" | "desc";
}

export default class TicketStore {
  mainStore: MainStore | null = null;

  tickets: ITicket[] = [];
  activeTicket: ITicketDetail | null = null;
  isLoading: boolean = false;
  isLoadingMore: boolean = false;
  isDetailLoading: boolean = false;
  replyLoading: boolean = false;
  reopenLoading: boolean = false;
  currentPage: number = 1;
  totalCount: number = 0;
  hasMoreTickets: boolean = true;
  keywordSearch: string = "";
  columnFilters: TicketColumnFilters | null = null;
  orderBy: TicketOrderBy | null = null;
  error: string | null = null;

  liveViewers: number = 0;
  // Responses that started under an older generation (search, filter, order
  // or reload since) describe a different list and must not be applied.
  private listGeneration = 0;
  // Bumped by each loadTicket; a live refetch that started before the latest
  // loadTicket (e.g. createReply's reload) must not overwrite its result.
  private detailSeq = new Map<number, number>();
  private seededTickets = new Map<number, { ticket: ITicket; seededAt: number }>();

  // Column settings state (visibility + pin/required), keyed by column id.
  columnSettings: TicketsTableColumnSetting[] = this.loadColumnSettings();

  constructor(mainStore: MainStore) {
    makeAutoObservable(this, {
      mainStore: false,
    });
    this.mainStore = mainStore;

    if (typeof window !== "undefined") {
      window.addEventListener(WS_EVENTS.TICKET_CHANGED, (e: Event) => {
        void this.handleTicketChanged((e as CustomEvent).detail);
      });
    }
    networkEventManager.on("connection:recovered", () => {
      void this.handleConnectionRecovered();
    });
  }

  setLiveViewing = (on: boolean) => {
    this.liveViewers = Math.max(0, this.liveViewers + (on ? 1 : -1));
  };

  private get isDefaultView(): boolean {
    return !this.hasFiltersApplied && !this.orderBy;
  }

  private nextDetailSeq = (id: number) => {
    const seq = (this.detailSeq.get(id) ?? 0) + 1;
    this.detailSeq.set(id, seq);
    return seq;
  };

  private static toRow(detail: ITicketDetail): ITicket {
    const { articles: _articles, files: _files, ...row } = detail;
    return row;
  }

  // Refetches for one ticket can resolve out of order; never let an older copy replace a newer one.
  private static isOlder(candidate: ITicket, current: ITicket) {
    return Date.parse(candidate.updated_at) < Date.parse(current.updated_at);
  }

  // The default order is newest first, so a ticket missing from a partly loaded
  // list is usually an older one on a later page, not a new one for the top.
  private sortsFirst = (row: ITicket) => {
    if (!this.hasMoreTickets) return true;
    const newest = this.tickets.find((t) => !this.seededTickets.has(t.id));
    return !newest || Date.parse(row.created_at) >= Date.parse(newest.created_at);
  };

  private upsertRow = (row: ITicket) => {
    const existing = this.tickets.find((t) => t.id === row.id);
    if (existing) {
      if (TicketStore.isOlder(row, existing)) return;
      this.tickets = this.tickets.map((t) => (t.id === row.id ? row : t));
    } else if (
      this.seededTickets.has(row.id) ||
      (this.isDefaultView && this.sortsFirst(row))
    ) {
      this.tickets = [row, ...this.tickets];
    }
    const seed = this.seededTickets.get(row.id);
    if (seed) seed.ticket = row;
  };

  seedTicket = (ticket: ITicket) => {
    this.seededTickets.set(ticket.id, { ticket, seededAt: Date.now() });
    this.tickets = [ticket, ...this.tickets.filter((t) => t.id !== ticket.id)];
  };

  private refreshTicketQuietly = async (id: number) => {
    const generation = this.listGeneration;
    const seq = this.detailSeq.get(id);
    try {
      const response = await apiService.getTicket(id);
      if (!response.success || !response.data) return;
      const detail = response.data;
      runInAction(() => {
        if (generation === this.listGeneration) {
          this.upsertRow(TicketStore.toRow(detail));
        }
        if (
          this.detailSeq.get(id) === seq &&
          this.activeTicket?.id === id &&
          !TicketStore.isOlder(detail, this.activeTicket)
        ) {
          this.activeTicket = detail;
        }
      });
    } catch (error) {
      console.error("[TicketStore] live ticket refresh failed:", error);
    }
  };

  handleTicketChanged = async (frame: TicketChangedFrame | null | undefined) => {
    if (this.liveViewers === 0 || typeof frame?.ticket_id !== "number") return;
    await this.refreshTicketQuietly(frame.ticket_id);
  };

  private handleConnectionRecovered = async () => {
    if (this.liveViewers === 0) return;
    const activeId = this.activeTicket?.id;
    await Promise.all([
      this.loadTickets(),
      activeId !== undefined ? this.refreshTicketQuietly(activeId) : undefined,
    ]);
  };

  private static getDefaultColumnSettings(): TicketsTableColumnSetting[] {
    return TICKETS_TABLE_COLUMN_OPTIONS.map((col) => ({
      id: col.id as TicketsTableColumnId,
      visible: col.visible !== false,
      required: col.required === true,
    }));
  }

  private loadColumnSettings(): TicketsTableColumnSetting[] {
    const defaults = TicketStore.getDefaultColumnSettings();
    try {
      const saved = localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
      if (saved) {
        const savedById = new Map<string, TicketsTableColumnSetting>(
          (JSON.parse(saved) as TicketsTableColumnSetting[]).map((setting) => [
            setting.id,
            setting,
          ]),
        );
        return defaults.map((def) => ({ ...def, ...savedById.get(def.id) }));
      }
    } catch {
      // Ignore localStorage errors
    }
    return defaults;
  }

  private saveColumnSettings(): void {
    try {
      localStorage.setItem(
        COLUMN_VISIBILITY_STORAGE_KEY,
        JSON.stringify(this.columnSettings),
      );
    } catch {
      // Ignore localStorage errors
    }
  }

  private getColumnSetting = (
    columnId: string,
  ): TicketsTableColumnSetting | undefined =>
    this.columnSettings.find((setting) => setting.id === columnId);

  private updateColumnSetting = (
    columnId: TicketsTableColumnId,
    updates: Partial<Omit<TicketsTableColumnSetting, "id">>,
  ) => {
    this.columnSettings = this.columnSettings.map((setting) =>
      setting.id === columnId ? { ...setting, ...updates } : setting,
    );
    this.saveColumnSettings();
  };

  toggleColumnVisibility = (columnId: TicketsTableColumnId) => {
    // The ticket number column is always required
    if (columnId === "number") return;
    this.updateColumnSetting(columnId, {
      visible: !this.getColumnSetting(columnId)?.visible,
    });
  };

  isColumnVisible = (columnId: string): boolean => {
    if (columnId === "number") return true;
    return this.getColumnSetting(columnId)?.visible !== false;
  };

  toggleRequiredColumn = (columnId: TicketsTableColumnId) => {
    if (columnId === "number") return;
    const nextRequired = !this.isColumnRequired(columnId);
    this.updateColumnSetting(
      columnId,
      nextRequired ? { required: true, visible: true } : { required: false },
    );
  };

  isColumnRequired = (columnId: string): boolean => {
    if (columnId === "number") return true;
    return !!this.getColumnSetting(columnId)?.required;
  };

  private extractTickets = (
    data: ITicket[] | { tickets: ITicket[]; tickets_count: number },
  ) => (Array.isArray(data) ? data : (data.tickets ?? []));

  setKeywordSearch = (value: string) => {
    this.keywordSearch = value;
    void this.loadTickets();
  };

  setOrderBy = (orderBy: TicketOrderBy | null) => {
    this.orderBy = orderBy;
    void this.loadTickets();
  };

  setColumnFilter = (
    columnId: string,
    values: string[],
    operator: FilterOperator = "and",
  ) => {
    const filteredValues = values.filter(Boolean);
    if (filteredValues.length === 0) {
      this.clearColumnFilter(columnId);
      return;
    }

    const filterType = COLUMN_FILTER_TYPES[columnId];
    this.columnFilters = {
      ...this.columnFilters,
      [columnId]:
        filterType === "dateRange" || filterType === "priceRange"
          ? filteredValues
          : { operator, values: filteredValues },
    };
    void this.loadTickets();
  };

  clearColumnFilter = (columnId: string) => {
    if (!this.columnFilters) return;
    const next = { ...this.columnFilters };
    delete next[columnId];
    this.columnFilters = Object.keys(next).length > 0 ? next : null;
    void this.loadTickets();
  };

  get hasFiltersApplied(): boolean {
    return (
      !!this.keywordSearch?.trim() ||
      (!!this.columnFilters && Object.keys(this.columnFilters).length > 0)
    );
  }

  clearFilters = () => {
    this.columnFilters = null;
    this.keywordSearch = "";
    void this.loadTickets();
  };

  loadTickets = async () => {
    const generation = ++this.listGeneration;
    runInAction(() => {
      this.isLoading = true;
      this.error = null;
      this.currentPage = 1;
      this.hasMoreTickets = true;
    });
    try {
      const response = await apiService.listTickets({
        page: 1,
        per_page: PER_PAGE,
        keywordSearch: this.keywordSearch,
        columnFilters: this.columnFilters,
        sort_by: this.orderBy?.field,
        sort_direction: this.orderBy?.direction,
      });
      runInAction(() => {
        if (generation !== this.listGeneration) return;
        if (response.success && response.data) {
          const data = response.data;
          const tickets = this.extractTickets(data);
          const totalCount = Array.isArray(data)
            ? tickets.length
            : (data.tickets_count ?? tickets.length);
          const returnedIds = new Set(tickets.map((t) => t.id));
          const now = Date.now();
          this.seededTickets.forEach((seed, id) => {
            if (returnedIds.has(id) || now - seed.seededAt > SEED_TTL_MS) {
              this.seededTickets.delete(id);
            }
          });
          // A just-filed ticket can lag the search index; keep it visible until
          // the server returns it, or until the TTL ends so a ticket the list
          // never returns does not stay pinned.
          const seeded = this.isDefaultView
            ? Array.from(this.seededTickets.values(), (seed) => seed.ticket)
            : [];
          this.tickets = [...seeded, ...tickets];
          this.totalCount = totalCount;
          this.hasMoreTickets = tickets.length < totalCount;
        } else {
          this.error = response.error || "Failed to load tickets";
        }
      });
    } catch (error) {
      runInAction(() => {
        if (generation !== this.listGeneration) return;
        this.error =
          error instanceof Error ? error.message : "Failed to load tickets";
      });
    } finally {
      runInAction(() => {
        if (generation === this.listGeneration) this.isLoading = false;
      });
    }
  };

  loadMoreTickets = async () => {
    if (this.isLoadingMore || !this.hasMoreTickets) return;

    const generation = this.listGeneration;
    runInAction(() => {
      this.isLoadingMore = true;
    });
    try {
      const nextPage = this.currentPage + 1;
      const response = await apiService.listTickets({
        page: nextPage,
        per_page: PER_PAGE,
        keywordSearch: this.keywordSearch,
        columnFilters: this.columnFilters,
        sort_by: this.orderBy?.field,
        sort_direction: this.orderBy?.direction,
      });
      runInAction(() => {
        if (generation !== this.listGeneration) return;
        if (response.success && response.data) {
          const data = response.data;
          const shownIds = new Set(this.tickets.map((t) => t.id));
          const newTickets = this.extractTickets(data).filter(
            (t) => !shownIds.has(t.id),
          );
          this.tickets = [...this.tickets, ...newTickets];
          this.currentPage = nextPage;
          const serverTotal = Array.isArray(data) ? null : data.tickets_count;
          this.totalCount = serverTotal ?? this.tickets.length;
          // Seeded and live-inserted rows are not in the server's total, so
          // count pages fetched rather than rows shown.
          this.hasMoreTickets =
            serverTotal != null && nextPage * PER_PAGE < serverTotal;
        } else {
          this.hasMoreTickets = false;
        }
      });
    } catch {
      runInAction(() => {
        if (generation === this.listGeneration) this.hasMoreTickets = false;
      });
    } finally {
      runInAction(() => {
        this.isLoadingMore = false;
      });
    }
  };

  /** Merges the freshly loaded activeTicket into its row in the tickets list. */
  private syncTicketInList = (ticketId: number) => {
    const detail = this.activeTicket;

    if (!detail || detail.id !== ticketId) {
      return;
    }
    
    const ticketFields = TicketStore.toRow(detail);
    runInAction(() => {
      this.tickets = this.tickets.map((t) =>
        t.id === ticketId ? { ...t, ...ticketFields } : t,
      );
    });
  };

  loadTicket = async (id: number) => {
    const seq = this.nextDetailSeq(id);
    runInAction(() => {
      this.isDetailLoading = true;
      this.error = null;
    });
    try {
      const response = await apiService.getTicket(id);
      runInAction(() => {
        if (this.detailSeq.get(id) !== seq) return;
        if (response.success && response.data) {
          this.activeTicket = response.data;
        } else {
          this.error = response.error || "Failed to load ticket";
        }
      });
    } catch (error) {
      runInAction(() => {
        if (this.detailSeq.get(id) !== seq) return;
        this.error =
          error instanceof Error ? error.message : "Failed to load ticket";
      });
    } finally {
      runInAction(() => {
        this.isDetailLoading = false;
      });
    }
  };

  createReply = async (
    ticketId: number,
    body: string,
    attachments?: File[],
  ): Promise<ApiResponse<unknown>> => {
    runInAction(() => {
      this.replyLoading = true;
    });
    try {
      // Upload first; a failed upload stops the reply so nothing is posted
      // without the files the user picked.
      const fileIds: string[] = [];
      for (const file of attachments ?? []) {
        const upload = await apiService.uploadFile(file);
        if (!upload.success || !upload.data?.file_id) {
          return {
            success: false,
            error: upload.error || `Couldn't upload "${file.name}"`,
          };
        }
        fileIds.push(upload.data.file_id);
      }

      const response = await apiService.createTicketArticle(ticketId, {
        body,
        fileIds,
      });
      if (response.success) {
        await this.loadTicket(ticketId);
      }
      return response;
    } finally {
      runInAction(() => {
        this.replyLoading = false;
      });
    }
  };

  reopenTicket = async (ticketId: number) => {
    runInAction(() => {
      this.reopenLoading = true;
    });
    try {
      const response = await apiService.reopenTicket(ticketId);
      if (response.success) {
        await this.loadTicket(ticketId);
        this.syncTicketInList(ticketId);
      }
      return response;
    } finally {
      runInAction(() => {
        this.reopenLoading = false;
      });
    }
  };
}
